import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import { db, functions } from '../firebase.js';
import { useAuth } from '../contexts/AuthContext.js';
import OasisBoard from './OasisBoard.js';
import OasisBoardSkeleton from './OasisBoardSkeleton.js';

// Same page as OnlineGame.js, pointed at practiceGames instead of
// onlineGames -- solo against the bot (always "player1" here, since a
// practice game has no second real player), same realtime-view/callable
// architecture, same round-end resolution UI.
export default function PracticeGame() {
  const { gameId } = useParams();
  const { currentUser } = useAuth();
  const [publicDoc, setPublicDoc] = useState(undefined);
  const [view, setView] = useState(undefined);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);

  useEffect(() => {
    document.body.classList.add('online-game-active');
    return () => document.body.classList.remove('online-game-active');
  }, []);

  useEffect(() => {
    const unsubPublic = onSnapshot(doc(db, 'practiceGames', gameId), snap => {
      setPublicDoc(snap.exists() ? snap.data() : null);
    });
    return unsubPublic;
  }, [gameId]);

  useEffect(() => {
    if (!currentUser) return undefined;
    const unsubView = onSnapshot(doc(db, 'practiceGames', gameId, 'views', currentUser.uid), snap => {
      setView(snap.exists() ? snap.data() : null);
    });
    return unsubView;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, currentUser?.uid]);

  // Advances the bot exactly one step at a time -- gives a real "Computer's
  // Turn" moment (and its own separate lastMoveCardIds highlight) instead of
  // the bot's whole response being folded invisibly into the human's own
  // move. No artificial delay here: the board's own incoming-move animation
  // (see OasisBoard.js) already provides the pacing once the bot's move
  // arrives.
  useEffect(() => {
    if (!publicDoc || publicDoc.status === 'completed') return undefined;
    const pendingStep = view?.roundEndResolution?.pendingStep;
    const isBotTurn = pendingStep
      ? pendingStep.deciderSide === 'opponent'
      : publicDoc.currentTurnPlayerId === publicDoc.player2Id;
    if (!isBotTurn) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const advancePracticeBotStep = httpsCallable(functions, 'advancePracticeBotStep');
        await advancePracticeBotStep({ gameId });
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gameId, publicDoc, view]);

  async function handleSubmitMove(payload) {
    setSubmitting(true);
    setError(null);
    try {
      const submitPracticeMove = httpsCallable(functions, 'submitPracticeMove');
      await submitPracticeMove({ gameId, ...payload });
    } catch (err) {
      setError(err.message);
      throw err; // let the board know the move didn't go through, so it keeps it staged
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmitRoundEndChoice(kind, choice) {
    setError(null);
    try {
      const submitPracticeRoundEndChoice = httpsCallable(functions, 'submitPracticeRoundEndChoice');
      await submitPracticeRoundEndChoice({ gameId, kind, choice });
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleAcknowledgeRoundEnd() {
    setAcknowledging(true);
    setError(null);
    try {
      const acknowledgePracticeRoundEnd = httpsCallable(functions, 'acknowledgePracticeRoundEnd');
      await acknowledgePracticeRoundEnd({ gameId });
    } catch (err) {
      setError(err.message);
    } finally {
      setAcknowledging(false);
    }
  }

  return (
    <div className="online-game-page">
      <div className="online-game-page-topbar">
        <Link to="/dev/practice-game" className="online-game-page-back">← Practice Games</Link>
      </div>

      {error && (
        <Alert variant="danger" className="online-game-page-alert" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {publicDoc === undefined || view === undefined ? (
        <div className="online-game-page-board">
          <OasisBoardSkeleton />
        </div>
      ) : !publicDoc || !view ? (
        <p>Game not found.</p>
      ) : (
        (() => {
          const myTurn = publicDoc.currentTurnPlayerId === publicDoc.player1Id && publicDoc.status !== 'completed';
          const myScore = publicDoc.scores.player1;
          const opponentScore = publicDoc.scores.player2;

          const lastRoundResult = view.lastRoundResult;
          const reviewPending = Boolean(lastRoundResult) && view.ackedRound < lastRoundResult.round;

          if (publicDoc.status === 'completed' && !reviewPending) {
            const result = myScore > opponentScore ? 'You won!' : myScore < opponentScore ? 'You lost.' : "It's a tie.";
            return (
              <div className="online-game-page-result">
                <h2>{result}</h2>
                <p>Final score: {myScore} to {opponentScore}</p>
                <Button as={Link} to="/dev/practice-game">Back to Practice Games</Button>
              </div>
            );
          }

          const displayView = reviewPending ? { hand: [], oasisRow: lastRoundResult.oasisRow } : view;
          const displayRound = reviewPending ? lastRoundResult.round : publicDoc.round;

          const roundSummary = reviewPending
            ? lastRoundResult.roundPoints.my === lastRoundResult.roundPoints.opponent
              ? `Round ${lastRoundResult.round} was a tie.`
              : lastRoundResult.roundPoints.my > lastRoundResult.roundPoints.opponent
              ? `You won round ${lastRoundResult.round}! (${lastRoundResult.roundPoints.my} to ${lastRoundResult.roundPoints.opponent})`
              : `The computer won round ${lastRoundResult.round}. (${lastRoundResult.roundPoints.my} to ${lastRoundResult.roundPoints.opponent})`
            : null;

          return (
            <div className="online-game-page-board">
              <OasisBoard
                view={displayView}
                myTurn={!reviewPending && myTurn}
                myName="You"
                opponentName="Computer"
                round={displayRound}
                myScore={myScore}
                opponentScore={opponentScore}
                opponentHandCount={publicDoc.player2HandCount}
                myPalmTreesRemaining={publicDoc.player1PalmTreesRemaining}
                opponentPalmTreesRemaining={publicDoc.player2PalmTreesRemaining}
                deckCount={reviewPending && lastRoundResult.deckCount !== null ? lastRoundResult.deckCount : publicDoc.deckCount}
                burnedCard={view.burnedCard}
                lastMoveCardIds={reviewPending ? lastRoundResult.flippedCardIds : publicDoc.lastMoveCardIds ?? []}
                revealResult={reviewPending}
                roundSummary={roundSummary}
                onNextRound={reviewPending ? handleAcknowledgeRoundEnd : undefined}
                nextRoundButtonLabel={lastRoundResult?.round >= 3 ? 'See Final Results' : 'Next Round'}
                nextRoundDisabled={acknowledging}
                onSubmitMove={handleSubmitMove}
                submitting={submitting}
                roundEndResolution={view.roundEndResolution}
                onSubmitRoundEndChoice={handleSubmitRoundEndChoice}
                lastRoundResult={lastRoundResult}
                lastMoveResult={view.lastMoveResult}
              />
            </div>
          );
        })()
      )}
    </div>
  );
}
